// src/features/dashboard/components/WeeklyHoursChart.tsx

import {
  useMemo,
  useState,
} from 'react';

import {
  useWeeklyHoursChart,
} from '../hooks/Employee/useWeeklyHoursChart';


/* ============================================================
 * TYPES
 * ============================================================ */

interface DailyHourItem {
  loggedDate: string;
  dayName: string;
  loggedHours: string | number;
}

interface WeeklyHoursData {
  workedHours: string;
  targetHours: string;
  compliancePercentage: number;
  dailyHours: DailyHourItem[];
}

interface ChartPoint {
  loggedDate: string;
  dayName: string;
  shortDay: string;
  loggedHours: number;
  hoursLabel: string;
  x: number;
  y: number;
}


/* ============================================================
 * HELPERS
 * ============================================================ */

const parseHours = (
  value:
    | string
    | number
    | null
    | undefined,
): number => {

  if (
    value === null ||
    value === undefined
  ) {
    return 0;
  }

  if (
    typeof value === 'number'
  ) {
    return Number.isFinite(value)
      ? value
      : 0;
  }

  const text =
    String(value).trim();

  if (!text) {
    return 0;
  }

  /*
   * Supports:
   *
   * 9h
   * 9.5h
   * 9h 30m
   * 09:30:00
   * 09:30
   * 9
   */

  if (
    text.includes(':')
  ) {

    const parts =
      text.split(':');

    const hours =
      Number(
        parts[0] ?? 0,
      );

    const minutes =
      Number(
        parts[1] ?? 0,
      );

    const seconds =
      Number(
        parts[2] ?? 0,
      );

    if (
      Number.isFinite(hours) &&
      Number.isFinite(minutes)
    ) {
      return (
        hours +
        minutes / 60 +
        seconds / 3600
      );
    }
  }

  const hoursMatch =
    text.match(
      /(\d+(?:\.\d+)?)\s*h/i,
    );

  const minutesMatch =
    text.match(
      /(\d+(?:\.\d+)?)\s*m/i,
    );

  if (
    hoursMatch ||
    minutesMatch
  ) {

    const hours =
      hoursMatch
        ? Number(
            hoursMatch[1],
          )
        : 0;

    const minutes =
      minutesMatch
        ? Number(
            minutesMatch[1],
          )
        : 0;

    return (
      hours +
      minutes / 60
    );
  }

  const numeric =
    Number(text);

  return Number.isFinite(
    numeric,
  )
    ? numeric
    : 0;
};


const formatHours = (
  hours: number,
): string => {

  if (
    !Number.isFinite(hours)
  ) {
    return '0h 0m';
  }

  const totalMinutes =
    Math.round(
      hours * 60,
    );

  const wholeHours =
    Math.floor(
      totalMinutes / 60,
    );

  const minutes =
    totalMinutes % 60;

  return `${wholeHours}h ${minutes}m`;
};


const formatCompactHours = (
  hours: number,
): string => {

  if (
    !Number.isFinite(hours)
  ) {
    return '0h';
  }

  const totalMinutes =
    Math.round(
      hours * 60,
    );

  const wholeHours =
    Math.floor(
      totalMinutes / 60,
    );

  const minutes =
    totalMinutes % 60;

  if (
    minutes === 0
  ) {
    return `${wholeHours}h`;
  }

  return `${wholeHours}h ${minutes}m`;
};


const getNiceStep = (
  maxValue: number,
): number => {

  if (
    maxValue <= 10
  ) {
    return 2;
  }

  if (
    maxValue <= 20
  ) {
    return 2;
  }

  if (
    maxValue <= 40
  ) {
    return 4;
  }

  if (
    maxValue <= 80
  ) {
    return 10;
  }

  return 20;
};


/* ============================================================
 * COMPONENT
 * ============================================================ */

const WeeklyHoursChart = () => {

  const {
    data,
    loading,
  } =
    useWeeklyHoursChart();


  const [
    activeIndex,
    setActiveIndex,
  ] =
    useState<number | null>(
      null,
    );


  const weeklyData =
    data as WeeklyHoursData | null;


  /* ==========================================================
   * DAILY TARGET
   * ========================================================== */

  const dailyTarget =
    useMemo(() => {

      if (!weeklyData) {
        return 0;
      }

      const target =
        parseHours(
          weeklyData.targetHours,
        );

      if (
        target <= 0
      ) {
        return 0;
      }

      return target / 5;

    }, [
      weeklyData,
    ]);


  /* ==========================================================
   * HOURS
   * ========================================================== */

  const hours =
    useMemo(() => {

      if (
        !weeklyData ||
        !Array.isArray(
          weeklyData.dailyHours,
        )
      ) {
        return [];
      }

      return weeklyData.dailyHours.map(
        (item) =>
          parseHours(
            item.loggedHours,
          ),
      );

    }, [
      weeklyData,
    ]);


  /* ==========================================================
   * Y AXIS
   *
   * Keep existing dynamic behavior.
   * ========================================================== */

  const yAxis =
    useMemo(() => {

      const highest =
        hours.length > 0
          ? Math.max(
              ...hours,
            )
          : 0;

      const requiredMax =
        Math.max(
          highest,
          dailyTarget,
          1,
        );

      const step =
        getNiceStep(
          requiredMax,
        );

      const max =
        Math.ceil(
          (
            requiredMax * 1.12
          ) /
            step,
        ) *
        step;

      const ticks: number[] =
        [];

      for (
        let value = 0;
        value <= max;
        value += step
      ) {

        ticks.push(
          Number(
            value.toFixed(2),
          ),
        );
      }

      return {
        min: 0,
        max,
        step,
        ticks,
      };

    }, [
      hours,
      dailyTarget,
    ]);


  /* ==========================================================
   * CHART POINTS
   * ========================================================== */

  const chartPoints =
    useMemo<ChartPoint[]>(
      () => {

        if (
          !weeklyData ||
          !Array.isArray(
            weeklyData.dailyHours,
          )
        ) {
          return [];
        }

        const items =
          weeklyData.dailyHours;

        const count =
          items.length;

        return items.map(
          (
            item,
            index,
          ) => {

            const loggedHours =
              parseHours(
                item.loggedHours,
              );

            /*
             * Small side padding.
             */

            const leftPadding =
              count === 1
                ? 50
                : 2;

            const rightPadding =
              count === 1
                ? 50
                : 2;

            const x =
              count === 1
                ? 50
                : leftPadding +
                  (
                    index /
                    Math.max(
                      count - 1,
                      1,
                    )
                  ) *
                  (
                    100 -
                    leftPadding -
                    rightPadding
                  );

            /*
             * Chart vertical area.
             */

            const bottom =
              92;

            const top =
              8;

            const height =
              bottom - top;

            const ratio =
              yAxis.max > 0
                ? loggedHours /
                  yAxis.max
                : 0;

            const y =
              bottom -
              ratio * height;

            return {
              loggedDate:
                item.loggedDate,

              dayName:
                item.dayName,

              shortDay:
                item.dayName
                  ?.slice(
                    0,
                    3,
                  ) ??
                '',

              loggedHours,

              hoursLabel:
                formatHours(
                  loggedHours,
                ),

              x,
              y,
            };
          },
        );

      },
      [
        weeklyData,
        yAxis.max,
      ],
    );


  /* ==========================================================
   * LINE
   * ========================================================== */

  const linePoints =
    useMemo(() => {

      return chartPoints
        .map(
          (point) =>
            `${point.x},${point.y}`,
        )
        .join(' ');

    }, [
      chartPoints,
    ]);


  /* ==========================================================
   * AREA
   * ========================================================== */

  const areaPoints =
    useMemo(() => {

      if (
        !chartPoints.length
      ) {
        return '';
      }

      const first =
        chartPoints[0];

      const last =
        chartPoints[
          chartPoints.length - 1
        ];

      if (
        !first ||
        !last
      ) {
        return '';
      }

      return [
        ...chartPoints.map(
          (point) =>
            `${point.x},${point.y}`,
        ),

        `${last.x},92`,
        `${first.x},92`,
      ].join(' ');

    }, [
      chartPoints,
    ]);


  /* ==========================================================
   * TARGET Y
   * ========================================================== */

  const targetY =
    useMemo(() => {

      if (
        dailyTarget <= 0 ||
        yAxis.max <= 0
      ) {
        return null;
      }

      const top =
        8;

      const bottom =
        92;

      const height =
        bottom - top;

      const ratio =
        dailyTarget /
        yAxis.max;

      return (
        bottom -
        ratio * height
      );

    }, [
      dailyTarget,
      yAxis.max,
    ]);


  /* ==========================================================
   * ACTIVE POINT
   * ========================================================== */

  const activePoint =
    activeIndex !== null
      ? chartPoints[
          activeIndex
        ]
      : null;


  /* ==========================================================
   * TOOLTIP POSITION
   * ========================================================== */

  const tooltipPosition =
    useMemo(() => {

      if (!activePoint) {
        return null;
      }

      const left =
        Math.min(
          84,
          Math.max(
            16,
            activePoint.x,
          ),
        );

      const showBelow =
        activePoint.y < 27;

      return {
        left:
          `${left}%`,

        top:
          showBelow
            ? `${Math.min(
                activePoint.y + 5,
                58,
              )}%`
            : `${Math.max(
                activePoint.y - 4,
                15,
              )}%`,

        transform:
          showBelow
            ? 'translate(-50%, 0)'
            : 'translate(-50%, -100%)',

        showBelow,
      };

    }, [
      activePoint,
    ]);


  /* ==========================================================
   * LOADING
   * ========================================================== */

  if (loading) {

    return (
      <div
        className="
          w-full
          min-w-0
          overflow-hidden
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-white
          p-3

          sm:p-4
        "
      >

        <div
          className="
            animate-pulse
          "
        >

          <div
            className="
              mb-3
              h-4
              w-44
              rounded
              bg-[#EAECF0]
            "
          />

          <div
            className="
              h-[220px]
              w-full
              rounded
              bg-[#F2F4F7]
            "
          />

        </div>

      </div>
    );
  }


  /* ==========================================================
   * EMPTY
   * ========================================================== */

  if (
    !weeklyData ||
    !Array.isArray(
      weeklyData.dailyHours,
    ) ||
    weeklyData.dailyHours
      .length === 0
  ) {

    return (
      <div
        className="
          w-full
          min-w-0
          overflow-hidden
          rounded-[16px]
          border
          border-[#D0D5DD]
          bg-white
          p-3

          sm:p-4
        "
      >

        <div
          className="
            flex
            h-[230px]
            items-center
            justify-center
            text-[11px]
            text-[#667085]
          "
        >
          No weekly hours data
          available
        </div>

      </div>
    );
  }


  /* ==========================================================
   * TARGET LABEL
   *
   * Example:
   * 9h Target
   * ========================================================== */

  const targetLabel =
    `${formatCompactHours(
      dailyTarget,
    )} Target`;


  /* ==========================================================
   * RENDER
   * ========================================================== */

  return (

    <div
      className="
        relative
        w-full
        min-w-0
        overflow-hidden
        rounded-[16px]
        border
        border-[#D0D5DD]
        bg-white
        p-3

        sm:p-4
      "
    >

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          mb-1
          flex
          min-w-0
          flex-wrap
          items-center
          gap-2
        "
      >

        <h2
          className="
            min-w-0
            truncate
            text-[13px]
            font-semibold
            leading-[18px]
            text-[#002B5B]

            sm:text-[14px]
          "
        >
          Weekly Hours vs Target
        </h2>


        <span
          className="
            shrink-0
            rounded-full
            bg-[#EEF4FF]
            px-2.5
            py-[3px]
            text-[8px]
            font-medium
            leading-[12px]
            text-[#2563EB]

            sm:text-[9px]
          "
        >
          This week · target{' '}
          {formatCompactHours(
            dailyTarget,
          )}{' '}
          / day
        </span>

      </div>


      {/* ======================================================
          CHART
      ====================================================== */}

      <div
        className="
          relative
          h-[235px]
          w-full
          min-w-0
          overflow-hidden

          sm:h-[240px]
        "
        onMouseLeave={() =>
          setActiveIndex(
            null,
          )
        }
      >

        {/* ====================================================
            Y AXIS
        ==================================================== */}

        <div
          className="
            absolute
            bottom-[38px]
            left-0
            top-[4px]
            z-[2]
            flex
            w-[32px]
            flex-col
            justify-between
            bg-white
            pr-[4px]
            text-right
          "
        >

          {[
            ...yAxis.ticks,
          ]
            .reverse()
            .map(
              (tick) => (

                <span
                  key={tick}
                  className="
                    whitespace-nowrap
                    text-[8px]
                    leading-none
                    text-[#667085]

                    sm:text-[9px]
                  "
                >
                  {
                    Number.isInteger(
                      tick,
                    )
                      ? `${tick}h`
                      : `${tick.toFixed(
                          1,
                        )}h`
                  }
                </span>

              ),
            )}

        </div>


        {/* ====================================================
            CHART AREA
        ==================================================== */}

        <div
          className="
            absolute
            bottom-[38px]
            left-[36px]
            right-[4px]
            top-[4px]
          "
        >

          <svg
            className="
              block
              h-full
              w-full
              overflow-visible
            "
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >

            {/* ==================================================
                GRID
            ================================================== */}

            {yAxis.ticks.map(
              (tick) => {

                const ratio =
                  yAxis.max > 0
                    ? tick /
                      yAxis.max
                    : 0;

                const y =
                  92 -
                  ratio * 84;

                return (

                  <line
                    key={tick}
                    x1="0"
                    y1={y}
                    x2="100"
                    y2={y}
                    stroke="#E9EDF2"
                    strokeWidth="0.5"
                    vectorEffect="non-scaling-stroke"
                  />

                );
              },
            )}


            {/* ==================================================
                BASELINE
            ================================================== */}

            <line
              x1="0"
              y1="92"
              x2="100"
              y2="92"
              stroke="#D0D5DD"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />


            {/* ==================================================
                AREA
            ================================================== */}

            {areaPoints && (

              <polygon
                points={
                  areaPoints
                }
                fill="#DCE6F5"
                fillOpacity="0.58"
              />

            )}


            {/* ==================================================
                TARGET LINE
            ================================================== */}

            {targetY !== null && (

              <line
                x1="0"
                y1={targetY}
                x2="100"
                y2={targetY}
                stroke="#F59E0B"
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />

            )}


            {/* ==================================================
                MAIN LINE
            ================================================== */}

            {linePoints && (

              <polyline
                points={
                  linePoints
                }
                fill="none"
                stroke="#244E78"
                strokeWidth="1.1"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />

            )}


            {/* ==================================================
                ACTIVE VERTICAL LINE
            ================================================== */}

            {activePoint && (

              <line
                x1={
                  activePoint.x
                }
                y1="0"
                x2={
                  activePoint.x
                }
                y2="92"
                stroke="#98A2B3"
                strokeWidth="1"
                strokeDasharray="2 2"
                vectorEffect="non-scaling-stroke"
              />

            )}


            {/* ==================================================
                SMALL ROUND POINTS
            ================================================== */}

            {chartPoints.map(
              (
                point,
                index,
              ) => {

                const isActive =
                  activeIndex ===
                  index;

                return (

                  <g
                    key={`${point.loggedDate}-${index}`}
                  >

                    {/* Hover target */}

                    <circle
                      cx={
                        point.x
                      }
                      cy={
                        point.y
                      }
                      r="6"
                      fill="transparent"
                      className="
                        cursor-pointer
                      "
                      onMouseEnter={() =>
                        setActiveIndex(
                          index,
                        )
                      }
                    />


                    {/* ==================================================
                        IMPORTANT:
                        ellipse instead of circle.

                        SVG is horizontally stretched with
                        preserveAspectRatio="none".

                        Using ellipse keeps the visible point
                        visually round/small.
                    ================================================== */}

                    <ellipse
                      cx={
                        point.x
                      }
                      cy={
                        point.y
                      }
                      rx={
                        isActive
                          ? 1.8
                          : 1.35
                      }
                      ry={
                        isActive
                          ? 2.2
                          : 1.65
                      }
                      fill="#244E78"
                      stroke="#244E78"
                      strokeWidth="0.7"
                      vectorEffect="non-scaling-stroke"
                      className="
                        pointer-events-none
                      "
                    />

                  </g>

                );
              },
            )}

          </svg>


          {/* ==================================================
              TARGET LABEL

              Shows:

              9h Target
          ================================================== */}

          {targetY !== null && (

            <div
              className="
                pointer-events-none
                absolute
                right-0
                z-[20]
                flex
                items-center
                gap-[3px]
                whitespace-nowrap
              "
              style={{
                top:
                  `${Math.max(
                    2,
                    targetY - 4,
                  )}%`,
              }}
            >

              <span
                className="
                  text-[9px]
                  font-medium
                  leading-none
                  text-[#F59E0B]

                  sm:text-[10px]
                "
              >
                {targetLabel}
              </span>

            </div>

          )}


          {/* ==================================================
              TOOLTIP
          ================================================== */}

          {activePoint &&
            tooltipPosition && (

              <div
                className="
                  pointer-events-none
                  absolute
                  z-[100]
                  max-w-[135px]
                "
                style={{
                  left:
                    tooltipPosition.left,

                  top:
                    tooltipPosition.top,

                  transform:
                    tooltipPosition.transform,
                }}
              >

                <div
                  className="
                    relative
                    min-w-[112px]
                    rounded-[6px]
                    border
                    border-[#D0D5DD]
                    bg-white
                    px-[9px]
                    py-[7px]
                    shadow-[0_3px_10px_rgba(16,24,40,0.14)]
                  "
                >

                  {/* Day */}

                  <div
                    className="
                      mb-[5px]
                      text-[9px]
                      font-medium
                      leading-[12px]
                      text-[#667085]
                    "
                  >
                    {activePoint.dayName}
                  </div>


                  {/* Hours */}

                  <div
                    className="
                      flex
                      items-center
                      gap-[6px]
                      whitespace-nowrap
                    "
                  >

                    <span
                      className="
                        h-[7px]
                        w-[7px]
                        shrink-0
                        rounded-full
                        bg-[#244E78]
                      "
                    />

                    <span
                      className="
                        text-[9px]
                        text-[#344054]
                      "
                    >
                      Hours:
                    </span>

                    <span
                      className="
                        font-semibold
                        text-[9px]
                        text-[#101828]
                    "
                    >
                      {
                        activePoint.hoursLabel
                      }
                    </span>

                  </div>


                  {/* Pointer */}

                  {tooltipPosition.showBelow ? (

                    <span
                      className="
                        absolute
                        -top-[4px]
                        left-1/2
                        h-[7px]
                        w-[7px]
                        -translate-x-1/2
                        rotate-45
                        border-l
                        border-t
                        border-[#D0D5DD]
                        bg-white
                      "
                    />

                  ) : (

                    <span
                      className="
                        absolute
                        -bottom-[4px]
                        left-1/2
                        h-[7px]
                        w-[7px]
                        -translate-x-1/2
                        rotate-45
                        border-r
                        border-b
                        border-[#D0D5DD]
                        bg-white
                      "
                    />

                  )}

                </div>

              </div>

            )}

        </div>


        {/* ====================================================
            X AXIS
        ==================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            bottom-0
            left-[36px]
            right-[4px]
            h-[36px]
          "
        >

          {chartPoints.map(
            (
              point,
              index,
            ) => {

              const isActive =
                activeIndex ===
                index;

              return (

                <div
                  key={`${point.loggedDate}-x`}
                  className="
                    absolute
                    top-0
                    flex
                    w-[42px]
                    -translate-x-1/2
                    justify-center
                  "
                  style={{
                    left:
                      `${point.x}%`,
                  }}
                >

                  {isActive ? (

                    <div
                      className="
                        relative
                        flex
                        h-[30px]
                        w-[40px]
                        items-center
                        justify-center
                        rounded-[2px]
                        border
                        border-[#98A2B3]
                        bg-[#F8FAFC]
                        text-[9px]
                        font-medium
                        text-[#344054]
                      "
                    >

                      {point.shortDay}

                      <span
                        className="
                          absolute
                          -top-[4px]
                          left-1/2
                          h-[7px]
                          w-[7px]
                          -translate-x-1/2
                          rotate-45
                          border-l
                          border-t
                          border-[#98A2B3]
                          bg-[#F8FAFC]
                        "
                      />

                    </div>

                  ) : (

                    <span
                      className="
                        flex
                        h-[30px]
                        w-[42px]
                        items-center
                        justify-center
                        text-[9px]
                        font-medium
                        text-[#667085]
                      "
                    >
                      {point.shortDay}
                    </span>

                  )}

                </div>

              );
            },
          )}

        </div>

      </div>


      {/* ======================================================
          FOOTER
      ====================================================== */}

      <div
        className="
          mt-0
          flex
          min-w-0
          items-center
          justify-between
          gap-2
          px-[1px]
        "
      >

        {/* TOTAL */}

        <p
          className="
            min-w-0
            truncate
            text-[9px]
            leading-[13px]
            text-[#667085]

            sm:text-[10px]
          "
        >
          Total this week:{' '}

          <span
            className="
              font-semibold
              text-[#344054]
            "
          >
            {weeklyData.workedHours}
          </span>

        </p>


        {/* TARGET */}

        <p
          className="
            shrink-0
            whitespace-nowrap
            text-[9px]
            leading-[13px]
            text-[#667085]

            sm:text-[10px]
          "
        >
          Target:{' '}

          <span
            className="
              font-semibold
              text-[#344054]
            "
          >
            {weeklyData.targetHours}
          </span>

        </p>


        {/* ACHIEVEMENT */}

        <p
          className={`
            shrink-0
            whitespace-nowrap
            text-[9px]
            leading-[13px]
            font-medium

            sm:text-[10px]

            ${
              Number(
                weeklyData.compliancePercentage,
              ) >= 100
                ? 'text-[#DC2626]'
                : 'text-[#16A34A]'
            }
          `}
        >
          ▲{' '}

          {Number(
            weeklyData.compliancePercentage,
          ).toFixed(2)}

          % achieved

        </p>

      </div>

    </div>
  );
};


export default WeeklyHoursChart;